// ============================================================
// IMPORT VALIDATION - Pure normalization functions for session
// and character import data. Merges partial/malformed data with
// factory defaults so the combat engine never receives
// incomplete objects.
// ============================================================
import type { Target, Tab, Slot, AtkProfiles, NarrativeEntry, BodyTypeId } from '../types';
import type { Character } from './character/types';
import { normalizeCharacter } from './character/normalize';
import { withImportableCharacterIds } from './importIds';
import { emptyTarget, emptySlot, blankProfile } from './factories';
import { BODY_TYPE_IDS } from './bodyConfig';

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

export interface ValidationWarning {
  field: string;
  message: string;
}

interface NormalizeTargetResult {
  target: Target;
  warnings: ValidationWarning[];
}

interface NormalizeTabResult {
  tab: Tab;
  warnings: ValidationWarning[];
}

interface NormalizeAtkProfilesResult {
  atkProfiles: AtkProfiles;
  warnings: ValidationWarning[];
}

interface NormalizeCharacterExportResult {
  characters: Character[];
  warnings: ValidationWarning[];
}

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

/**
 * Deep-merge a partial nested object into a defaults object.
 * Only copies keys that exist in `defaults`. Primitive values
 * in `partial` override defaults; nested objects are recursively
 * merged. Returns the merged result (mutates `defaults` in place).
 */
// Fields whose non-zero default is still a harmless backfill for older
// exports (×1 multipliers = "no unnatural characteristic"; SB/WPB 3 =
// the human baseline every pre-v8.197.3 target implicitly had). Listed
// explicitly so genuinely meaningful defaults keep warning.
const TRIVIAL_BACKFILL_FIELDS = new Set([
  'unnaturalAgility',
  'unnaturalWeaponSkill',
  'unnaturalStrength',
  'unnaturalWillpower',
  'strengthBonus',
  'willpowerBonus',
]);

/** Zero-values are harmless defaults - older exports simply didn't have these fields yet. */
const TRIVIAL_VALUES = new Set<unknown>([false, 0, null, undefined, '']);

/** True when a missing field's default is not worth a warning. */
function isTrivialDefault(key: string, def: unknown): boolean {
  if (TRIVIAL_BACKFILL_FIELDS.has(key)) return true;
  return Array.isArray(def) ? def.length === 0 : TRIVIAL_VALUES.has(def);
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

/** One present field: nested objects (wounds, armour, dodge, parry, etc.) merge; primitives and arrays are taken as-is. */
function mergedValue(defaultVal: unknown, partialVal: unknown, path: string, warnings: ValidationWarning[]): unknown {
  if (!isPlainObject(defaultVal)) return partialVal;
  return deepMerge({ ...defaultVal }, partialVal as Record<string, unknown>, path, warnings);
}

function deepMerge<T extends Record<string, unknown>>(
  defaults: T,
  partial: Record<string, unknown> | null | undefined,
  path: string,
  warnings: ValidationWarning[],
): T {
  if (!partial || typeof partial !== 'object') return defaults;
  const merged = defaults as Record<string, unknown>;
  for (const key of Object.keys(defaults)) {
    if (key in partial) merged[key] = mergedValue(merged[key], partial[key], `${path}.${key}`, warnings);
    else if (!isTrivialDefault(key, merged[key]))
      warnings.push({ field: `${path}.${key}`, message: `missing, using default` });
  }
  return defaults;
}

// ────────────────────────────────────────────────────────────
// Target normalization
// ────────────────────────────────────────────────────────────

/**
 * The body type a raw target declares, so defaults use the correct armour
 * shape. Old exports without bodyType default to 'humanoid' - their armour
 * keys ({ head, rightArm, ... }) match the humanoid default exactly.
 * Backward compat: infer 'horde' from horde.isHorde when bodyType is absent.
 */
function detectBodyType(obj: Record<string, unknown>): BodyTypeId {
  if (typeof obj.bodyType === 'string' && BODY_TYPE_IDS.includes(obj.bodyType as BodyTypeId)) {
    return obj.bodyType as BodyTypeId;
  }
  return isObject(obj.horde) && obj.horde.isHorde === true ? 'horde' : 'humanoid';
}

/** Any non-null object (arrays included), as the legacy horde check read it. */
const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';

/**
 * Merge a potentially incomplete target object with emptyTarget()
 * defaults. Every missing field gets the default value; existing
 * fields are preserved. Nested objects (wounds, armour, dodge,
 * parry, forceField, cover, horde) are deep-merged.
 */
export function normalizeTarget(raw: unknown): NormalizeTargetResult {
  const warnings: ValidationWarning[] = [];

  if (!raw || typeof raw !== 'object') {
    warnings.push({ field: 'target', message: 'missing or invalid, using full defaults' });
    return { target: emptyTarget(), warnings };
  }

  const obj = raw as Record<string, unknown>;
  const bodyType = detectBodyType(obj);
  // Inject resolved bodyType into the partial so deepMerge sees it as present
  // (avoids spurious "missing, using default" warning for old exports)
  const enrichedObj = { ...obj, bodyType };
  const defaults = emptyTarget(bodyType) as unknown as Record<string, unknown>;
  const target = deepMerge(defaults, enrichedObj, 'target', warnings);
  // THE LEGACY TYPE IMPLIES THE FLAG, but never clears it. This used to
  // force `isHorde = bodyType === 'horde'`, which silently demoted a
  // horde of vehicles back to a single vehicle on the way in - so even a
  // hand-edited file could not express one (v8.44.0).
  const t = target as unknown as Target;
  if (t.bodyType === 'horde' && t.horde) t.horde.isHorde = true;

  return { target: t, warnings };
}

// ────────────────────────────────────────────────────────────
// Tab normalization
// ────────────────────────────────────────────────────────────

/**
 * Normalize a tab: ensure narrativeLog is an array, label exists,
 * target is normalized via normalizeTarget().
 */
export function normalizeTab(raw: unknown): NormalizeTabResult {
  const warnings: ValidationWarning[] = [];
  if (!raw || typeof raw !== 'object') {
    warnings.push({ field: 'tab', message: 'missing or invalid, using full defaults' });
    const target = emptyTarget();
    return {
      tab: { id: Date.now().toString(), label: 'Target', target, narrativeLog: [], sourceCharacter: null },
      warnings,
    };
  }
  const obj = raw as Record<string, unknown>;
  const id = tabId(obj, warnings);
  const { target, warnings: targetWarnings } = normalizeTarget(obj.target);
  warnings.push(...targetWarnings);
  const label = tabLabel(obj, target, warnings);
  const narrativeLog = tabNarrativeLog(obj, warnings);
  // Source character (pass through as-is)
  const sourceCharacter = (obj.sourceCharacter as Character | null) ?? null;
  return { tab: { id, label, target, narrativeLog, sourceCharacter }, warnings };
}

/** The tab's id, or a fresh one (with a warning) when it has none. */
function tabId(obj: Record<string, unknown>, warnings: ValidationWarning[]): string {
  if (typeof obj.id === 'string' && obj.id) return obj.id;
  const id = Date.now().toString();
  warnings.push({ field: 'tab.id', message: 'missing, generated new id' });
  return id;
}

/** The tab's label, or the target's name (with a warning) when it has none. */
function tabLabel(obj: Record<string, unknown>, target: Target, warnings: ValidationWarning[]): string {
  if (typeof obj.label === 'string' && obj.label) return obj.label;
  const label = target.name || 'Target';
  warnings.push({ field: 'tab.label', message: `missing, defaulted to "${label}"` });
  return label;
}

/** The narrative log; anything but an array becomes [] (warning only when something was there). */
function tabNarrativeLog(obj: Record<string, unknown>, warnings: ValidationWarning[]): NarrativeEntry[] {
  if (Array.isArray(obj.narrativeLog)) return obj.narrativeLog as NarrativeEntry[];
  if (obj.narrativeLog !== undefined) {
    warnings.push({ field: 'tab.narrativeLog', message: 'not an array, defaulted to []' });
  }
  return [];
}

// ────────────────────────────────────────────────────────────
// Attack profiles normalization
// ────────────────────────────────────────────────────────────

/**
 * Validate and normalize atkProfiles structure.
 * Ensures `list` exists and each profile is an array of exactly
 * 40 slots (padding short arrays, truncating long ones).
 */
export function normalizeAtkProfiles(raw: unknown): NormalizeAtkProfilesResult {
  const warnings: ValidationWarning[] = [];
  if (!raw || typeof raw !== 'object') {
    warnings.push({ field: 'atkProfiles', message: 'missing or invalid, using default' });
    return { atkProfiles: { current: 'Default', list: { Default: blankProfile() } }, warnings };
  }
  const obj = raw as Record<string, unknown>;
  const current = currentProfileName(obj, warnings);
  if (!obj.list || typeof obj.list !== 'object') {
    warnings.push({ field: 'atkProfiles.list', message: 'missing, using empty default profile' });
    return { atkProfiles: { current, list: { Default: blankProfile() } }, warnings };
  }
  const list = normalizeProfiles(obj.list as Record<string, unknown>, warnings);
  // Ensure current profile exists in list
  if (!(current in list)) return { atkProfiles: fallbackCurrent(current, list, warnings), warnings };
  return { atkProfiles: withCharBindings({ current, list }, obj._charBindings), warnings };
}

/** The selected profile's name, or 'Default' (with a warning) when absent. */
function currentProfileName(obj: Record<string, unknown>, warnings: ValidationWarning[]): string {
  if (typeof obj.current === 'string' && obj.current) return obj.current;
  warnings.push({ field: 'atkProfiles.current', message: 'missing, defaulted to "Default"' });
  return 'Default';
}

/** Every profile compacted to its armed weapons; a non-array becomes a blank profile. */
function normalizeProfiles(list: Record<string, unknown>, warnings: ValidationWarning[]): Record<string, Slot[]> {
  const normalizedList: Record<string, Slot[]> = {};
  for (const [profileName, profileSlots] of Object.entries(list)) {
    normalizedList[profileName] = normalizeProfile(profileName, profileSlots, warnings);
  }
  return normalizedList;
}

/** One profile's armed weapons, warning when cells were dropped or it was not a list at all. */
function normalizeProfile(profileName: string, profileSlots: unknown, warnings: ValidationWarning[]): Slot[] {
  const field = `atkProfiles.list["${profileName}"]`;
  if (!Array.isArray(profileSlots)) {
    warnings.push({ field, message: 'not an array, replaced with blank profile' });
    return blankProfile();
  }
  const slots = armedSlots(profileSlots);
  if (slots.length !== profileSlots.length) {
    const kept = `kept ${slots.length} armed weapon${slots.length === 1 ? '' : 's'}`;
    warnings.push({ field, message: `had ${profileSlots.length} cells, ${kept}` });
  }
  return slots;
}

/**
 * Absent is not portable: a v1 file may omit `isEmpty` altogether, so a
 * cell counts as armed when it SAYS so or when it names a weapon. `name`
 * is the pre-`weaponName` spelling and is still the fallback
 * `weaponFromSlot` reads, so a cell carrying only that is armed too.
 */
function isArmed(cell: Partial<Slot>): boolean {
  if (cell.isEmpty === false) return true;
  return cell.isEmpty === undefined && !!(cell.weaponName || cell.name);
}

/**
 * COMPACT, do not pad. A v1-v3 file is a 40-cell grid, mostly empty; a
 * rack holds only what is armed (v8.189.0). A foreign `weaponId` would
 * collide with a live combatant's ammo ledger, so the import mints its own
 * on the way through.
 */
function armedSlots(cells: unknown[]): Slot[] {
  const slots: Slot[] = [];
  for (const raw of cells) {
    if (!raw || typeof raw !== 'object' || !isArmed(raw as Partial<Slot>)) continue;
    slots.push({
      ...emptySlot(slots.length),
      ...(raw as Partial<Slot>),
      id: slots.length,
      isEmpty: false,
      weaponId: undefined,
    });
  }
  return slots;
}

/** When the named profile is missing: the first there is, or a fresh Default when the list is empty. */
function fallbackCurrent(current: string, list: Record<string, Slot[]>, warnings: ValidationWarning[]): AtkProfiles {
  const firstProfile = Object.keys(list)[0];
  if (firstProfile) {
    warnings.push({ field: 'atkProfiles.current', message: `"${current}" not found in list, using "${firstProfile}"` });
    return { current: firstProfile, list };
  }
  warnings.push({ field: 'atkProfiles.list', message: 'empty, using default profile' });
  list['Default'] = blankProfile();
  return { current: 'Default', list };
}

/** Preserve _charBindings if present (serializable character binding metadata). */
function withCharBindings(result: AtkProfiles, charBindings: unknown): AtkProfiles {
  if (charBindings && typeof charBindings === 'object') {
    (result as unknown as Record<string, unknown>)._charBindings = charBindings;
  }
  return result;
}

// ────────────────────────────────────────────────────────────
// Character export normalization
// ────────────────────────────────────────────────────────────

/**
 * Normalize character export data from either solo (v1 envelope)
 * or multiplayer (raw array) format into a consistent array.
 * Skips entries missing `id` or `name`; an id no document could carry is
 * replaced by a stand-in (`importIds`), since what this reads is sent to the
 * session as `characters/{id}`.
 */
export function normalizeCharacterExport(data: unknown): NormalizeCharacterExportResult {
  const warnings: ValidationWarning[] = [];
  if (!data) {
    warnings.push({ field: 'characters', message: 'no data provided' });
    return { characters: [], warnings };
  }
  const rawChars = rawCharacterList(withImportableCharacterIds(data), warnings);
  if (!rawChars) return { characters: [], warnings };
  const characters: Character[] = [];
  // An index loop, not forEach: a hole in the list is an invalid entry, not nothing.
  for (let i = 0; i < rawChars.length; i++) {
    const character = characterAt(rawChars[i], i, warnings);
    if (character) characters.push(character);
  }
  return { characters, warnings };
}

/** The raw entries: a bare array (multiplayer), a v1 envelope's list, or one object wrapped; null when none of these. */
function rawCharacterList(data: unknown, warnings: ValidationWarning[]): unknown[] | null {
  if (Array.isArray(data)) return data;
  if (typeof data !== 'object' || data === null) {
    warnings.push({ field: 'characters', message: 'invalid data format' });
    return null;
  }
  const obj = data as Record<string, unknown>;
  // v1 envelope format { version, characters, exportedAt }
  if (Array.isArray(obj.characters)) return obj.characters;
  warnings.push({ field: 'characters', message: 'single object detected, wrapped in array' });
  return [data];
}

/** One entry as a character, normalized on ingest (legacy skill shapes migrate, v6.30.0); null when skipped. */
function characterAt(entry: unknown, i: number, warnings: ValidationWarning[]): Character | null {
  if (!entry || typeof entry !== 'object') {
    warnings.push({ field: `characters[${i}]`, message: 'invalid entry, skipped' });
    return null;
  }
  const obj = entry as Record<string, unknown>;
  if (!obj.id || !obj.name) {
    warnings.push({ field: `characters[${i}]`, message: 'missing id or name, skipped' });
    return null;
  }
  return normalizeCharacter(entry);
}
